import React, { useState } from 'react'

import {
  BenefitParticipationDecision,
  CompanyUserBenefitDetailsResponse,
  MakeOptInOptOutDecisionRequest,
  makeOptInOptOutDecision,
} from 'core'

import { Alert, Button, Card, Col, Row } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequestV2, useLoginUserV2 } from 'common/hooks'

import AllowancesStatistics from '../../../common/AllowancesStatistics'
import AnnualInsuranceCoverage from '../../../common/annualInsuranceCoverage'
import BenefitDetailsSummary from '../../../common/benefitSummary/BenefitSummary'
import CurrentBalance from '../../../common/currentBalance/CurrentBalance'
import Marketplace from '../../../common/marketplace'
import Premiums from '../../../common/premiums'
import ReimbursementRequests from '../../../common/reimbursementRequests'
import ShopForPolicy from '../../../common/shopForPolicy/ShopForPolicy'
import TransactionHistory from '../../../common/transactionHistory'
import OptOutConfirmModal from '../common/optOutConfirmModal'

type OptInDashboardProps = {
  companyUserBenefitDetails: CompanyUserBenefitDetailsResponse
}
export default function OptInDashboard({ companyUserBenefitDetails }: OptInDashboardProps) {
  const { user } = useLoginUserV2()
  const [showOptOutModal, setShowOptOutModal] = useState(false)
  const { sendCommand: sendMakeDecision, isLoading: isMakeDecisionLoading } = useCommandRequestV2(
    (request: MakeOptInOptOutDecisionRequest) => makeOptInOptOutDecision(request),
  )

  const onOptOut = async () => {
    const result = await sendMakeDecision({
      planPeriodId: companyUserBenefitDetails.planPeriodId,
      decision: BenefitParticipationDecision.OptedOut,
    })
    if (result.completed) {
      window.location.reload()
    }
  }

  return (
    <>
      {companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision &&
        !companyUserBenefitDetails.isMaxDateToTakeBenefitParticipationDecisionReached &&
        !companyUserBenefitDetails.hasBenefitStarted && (
          <Card className={'opt-out-dashboard-wrap mb-16'}>
            <p>Welcome, {user.firstName}</p>
            <h2 className={'text-bold'}>What’s new for today</h2>
            <Alert
              type={'success'}
              message={
                <Row gutter={16} align={'middle'} justify={'space-between'}>
                  <Col>
                    You have <b>OPTED IN</b> to your ICHRA benefits. You have until{' '}
                    <b>{parseZoneFormat(companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision)}</b> to
                    change your decision and opt-out of the ICHRA.
                  </Col>

                  <Col>
                    <Button
                      type={'default'}
                      onClick={() => setShowOptOutModal(true)}
                      loading={isMakeDecisionLoading}
                      disabled={isMakeDecisionLoading}
                    >
                      Opt Out
                    </Button>
                  </Col>
                </Row>
              }
            ></Alert>
          </Card>
        )}

      {!companyUserBenefitDetails.hasBenefitStarted && (
        <Row gutter={16}>
          <Col span={12}>
            <CurrentBalance planPeriodId={companyUserBenefitDetails.planPeriodId} />
          </Col>

          <Col span={12}>
            <AnnualInsuranceCoverage
              planPeriodId={companyUserBenefitDetails.planPeriodId}
              benefitEligibilityDate={companyUserBenefitDetails.benefitEligibilityDate}
              hasBenefitStarted={companyUserBenefitDetails.hasBenefitStarted}
            />
          </Col>
        </Row>
      )}

      {companyUserBenefitDetails.hasBenefitStarted && (
        <Row gutter={16} className={'mb-16'}>
          <Col span={12}>
            <CurrentBalance
              planPeriodId={companyUserBenefitDetails.planPeriodId}
              reimburseButtonDisabled={!companyUserBenefitDetails.hasBenefitStarted}
            />

            <AnnualInsuranceCoverage
              planPeriodId={companyUserBenefitDetails.planPeriodId}
              benefitEligibilityDate={companyUserBenefitDetails.benefitEligibilityDate}
              hasBenefitStarted={companyUserBenefitDetails.hasBenefitStarted}
            />
          </Col>

          <Col span={12}>
            <AllowancesStatistics planPeriodId={companyUserBenefitDetails.planPeriodId} />
          </Col>
        </Row>
      )}

      <BenefitDetailsSummary
        planPeriodStartDate={companyUserBenefitDetails.planPeriodStartDate}
        planPeriodEndDate={companyUserBenefitDetails.planPeriodEndDate}
        primaryBenefitContactName={companyUserBenefitDetails.primaryBenefitContact.name}
        primaryBenefitContactEmail={companyUserBenefitDetails.primaryBenefitContact.workEmail}
        familyStatus={companyUserBenefitDetails.familyStatus.name}
        classDetails={companyUserBenefitDetails.classDetails.name}
        expenseType={companyUserBenefitDetails.classDetails.expenseType.name}
        monthlyAllowance={companyUserBenefitDetails.classDetails.monthlyAllowance}
      />

      <section className={'w-100 mb-16'}>
        <ShopForPolicy user={user} isIchra />
      </section>

      <section className={'mb-16'}>
        <Marketplace />
      </section>

      {companyUserBenefitDetails.hasBenefitStarted && (
        <>
          <div className="mb-16">
            <ReimbursementRequests planPeriodId={companyUserBenefitDetails.planPeriodId} />
          </div>
          <div className="mb-16">
            <TransactionHistory planPeriodId={companyUserBenefitDetails.planPeriodId} />
          </div>
        </>
      )}

      <Premiums planPeriodId={companyUserBenefitDetails.planPeriodId} />

      {companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision && (
        <OptOutConfirmModal
          visible={showOptOutModal}
          hasBenefitStarted={companyUserBenefitDetails.hasBenefitStarted}
          maxDateToTakeBenefitParticipationDecision={
            companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision
          }
          onOk={onOptOut}
          okButtonDisabled={isMakeDecisionLoading}
          okButtonLoading={isMakeDecisionLoading}
          onCancel={() => setShowOptOutModal(false)}
        />
      )}
    </>
  )
}
